import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from './axiosInstance';

/**
 * ActivityLog — admin-only feed of every forecast change (who did what,
 * for which customer, when). Reads GET /forecast/activity, which formats the
 * existing audit log into plain sentences. Visibility is gated by the email
 * allowlist both here (tab hidden) and on the server (the real gate).
 */
function ActivityLog() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [people, setPeople] = useState([]);
  const [companies, setCompanies] = useState([]);

  const [filters, setFilters] = useState({ userId: '', company: '', action: '', startDate: '', endDate: '' });
  const [page, setPage] = useState(1);
  const LIMIT = 50;

  const fetchActivity = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: LIMIT };
      if (filters.userId) params.userId = filters.userId;
      if (filters.company) params.company = filters.company;
      if (filters.action) params.action = filters.action;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      const res = await axiosInstance.get('/forecast/activity', { params });
      setData(res.data);
      if (res.data.people) setPeople(res.data.people);
      if (res.data.companies) setCompanies(res.data.companies);
      setError(null);
    } catch (err) {
      console.error('Error fetching activity:', err);
      setError(err.response?.status === 403
        ? 'You are not authorized to view the activity log.'
        : 'Failed to load activity log');
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => { fetchActivity(); }, [fetchActivity]);

  const setFilter = (key, val) => { setFilters(prev => ({ ...prev, [key]: val })); setPage(1); };
  const clearFilters = () => { setFilters({ userId: '', company: '', action: '', startDate: '', endDate: '' }); setPage(1); };
  const hasFilters = filters.userId || filters.company || filters.action || filters.startDate || filters.endDate;

  const relTime = (ts) => {
    const d = new Date(ts);
    const secs = Math.floor((Date.now() - d.getTime()) / 1000);
    if (secs < 60) return 'just now';
    const mins = Math.floor(secs / 60);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  };
  const absTime = (ts) => new Date(ts).toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });

  const actionMeta = (a) => {
    if (a === 'CREATE') return { label: 'Added', cls: 'srf-act-create' };
    if (a === 'DELETE') return { label: 'Deleted', cls: 'srf-act-delete' };
    return { label: 'Updated', cls: 'srf-act-update' };
  };

  return (
    <div className="srf-store-types-section">
      <div className="srf-section-header">
        <h2>Activity Log</h2>
        {data && (
          <div className="srf-section-header-right">
            <span className="srf-act-count">{data.total.toLocaleString()} change{data.total === 1 ? '' : 's'}</span>
          </div>
        )}
      </div>

      <p className="srf-covered-legend">
        Every add, edit, and delete across the forecast — who did it, for which customer, and when.
      </p>

      {/* Filters */}
      <div className="srf-act-filters">
        <div className="srf-act-filter">
          <label>Person</label>
          <select value={filters.userId} onChange={e => setFilter('userId', e.target.value)}>
            <option value="">Everyone</option>
            {people.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div className="srf-act-filter">
          <label>Customer</label>
          <select value={filters.company} onChange={e => setFilter('company', e.target.value)}>
            <option value="">All customers</option>
            {companies.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="srf-act-filter">
          <label>Action</label>
          <select value={filters.action} onChange={e => setFilter('action', e.target.value)}>
            <option value="">All</option>
            <option value="CREATE">Added</option>
            <option value="UPDATE">Updated</option>
            <option value="DELETE">Deleted</option>
          </select>
        </div>
        <div className="srf-act-filter">
          <label>From</label>
          <input type="date" value={filters.startDate} onChange={e => setFilter('startDate', e.target.value)} />
        </div>
        <div className="srf-act-filter">
          <label>To</label>
          <input type="date" value={filters.endDate} onChange={e => setFilter('endDate', e.target.value)} />
        </div>
        {hasFilters && (
          <button className="srf-btn srf-btn-sm srf-btn-secondary srf-act-clear" onClick={clearFilters}>
            Clear filters
          </button>
        )}
      </div>

      {error && <div className="srf-error" style={{ marginBottom: 12 }}>{error}</div>}

      {loading ? (
        <div className="srf-loading">Loading...</div>
      ) : !data || data.activities.length === 0 ? (
        <div className="srf-empty">
          {hasFilters ? 'No activity matches these filters.' : 'No forecast activity recorded yet.'}
        </div>
      ) : (
        <>
          <div className="srf-act-list">
            {data.activities.map(a => {
              const meta = actionMeta(a.action);
              return (
                <div key={a.id} className="srf-act-row">
                  <span className={`srf-act-pill ${meta.cls}`}>{meta.label}</span>
                  <span className="srf-act-text">
                    <span className="srf-act-actor">{a.actorName}</span> {a.text}
                  </span>
                  <span className="srf-act-time" title={absTime(a.timestamp)}>{relTime(a.timestamp)}</span>
                </div>
              );
            })}
          </div>

          {data.pages > 1 && (
            <div className="srf-act-pager">
              <button className="srf-btn srf-btn-sm srf-btn-secondary" disabled={page <= 1}
                onClick={() => setPage(p => Math.max(1, p - 1))}>‹ Newer</button>
              <span className="srf-act-pageinfo">Page {data.page} of {data.pages}</span>
              <button className="srf-btn srf-btn-sm srf-btn-secondary" disabled={page >= data.pages}
                onClick={() => setPage(p => p + 1)}>Older ›</button>
            </div>
          )}

          {data.capped && (
            <p className="srf-act-capnote">
              Showing the most recent {(3000).toLocaleString()} changes. Narrow by date to see older activity.
            </p>
          )}
        </>
      )}
    </div>
  );
}

export default ActivityLog;
